// ────────────────────────────────────────────────────────────────────────────
// myI panel content — DERIVED FROM REAL ROWS.
//
// Turns the live To-Book rows (with their recommended route + cutoff from
// `buildBookingRows` / `recommendRoute`) into the panel's two order-level
// sections:
//   • Exceptions (Urgent) — no ferry route on file, or the recommended
//     sailing's port cutoff has already passed: a manual decision is needed.
//   • Proposals (Backlog) — a still-catchable recommendation: myI suggests
//     booking it on that route, soonest-cutoff first.
//
// The Capacity swaps + capacity forecast sections still need operator / block
// capacity data we don't have yet, so they stay on the parked mock.
// ────────────────────────────────────────────────────────────────────────────

import type { BookingException, BookingProposal, BookingRow } from './mockData';

/** "8h 10m" / "45m" from a minute count. */
function fmtDur(min: number): string {
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${String(r).padStart(2, '0')}m` : `${r}m`;
}

/** A short cargo-specific caveat for the proposal note, if any. */
function cargoNote(cargoLabel: string): string | null {
  if (/hazard/i.test(cargoLabel)) return 'Hazardous — DGN forward before cutoff';
  if (/refrig|temperature|gdp/i.test(cargoLabel)) return 'Temperature-controlled — plug declared';
  return null;
}

export interface Intelligence {
  exceptions: BookingException[];
  proposals: BookingProposal[];
}

export function buildIntelligence(rows: BookingRow[]): Intelligence {
  const exceptions: BookingException[] = [];
  const proposals: { proposal: BookingProposal; cutoff: number }[] = [];

  for (const row of rows) {
    const cutoff = row.cutoffMinutes;

    // No route on file for the crossing → manual routing.
    if (!row.best) {
      exceptions.push({
        id: row.id,
        orderNumber: row.orderNumber,
        customer: row.customer,
        corridor: row.corridor,
        cargoLabel: row.cargoLabel,
        window: row.window,
        reason: `${row.noFitReason ?? 'No ferry route on file for this crossing'}. Manual routing needed.`,
      });
      continue;
    }

    // Recommended sailing's cutoff has passed → can't make it; manual decision.
    if (typeof cutoff === 'number' && cutoff <= 0) {
      exceptions.push({
        id: row.id,
        orderNumber: row.orderNumber,
        customer: row.customer,
        corridor: row.corridor,
        cargoLabel: row.cargoLabel,
        window: row.window,
        reason: `Recommended ${row.best.routeShort} — port cutoff passed ${fmtDur(-cutoff)} ago. No catchable sailing for the ${
          row.window === 'Any' ? 'delivery' : row.window
        } window; manual decision needed.`,
      });
      continue;
    }

    // Catchable recommendation → propose booking it on the recommended route.
    const urgent = typeof cutoff === 'number' && cutoff < 120;
    const note = [
      typeof cutoff === 'number' ? `${urgent ? 'Urgent — ' : ''}port cutoff in ${fmtDur(cutoff)}` : null,
      row.customs ? 'Customs leg' : null,
      cargoNote(row.cargoLabel),
    ]
      .filter(Boolean)
      .join(' · ');

    proposals.push({
      cutoff: typeof cutoff === 'number' ? cutoff : Number.POSITIVE_INFINITY,
      proposal: {
        id: row.id,
        orderNumber: row.orderNumber,
        customer: row.customer,
        routeShort: row.best.routeShort,
        // The specific sailing is chosen at booking — the route is the proposal.
        sailTime: row.best.sailTime ?? '',
        operator: row.best.operator,
        type: row.best.type,
        roadKm: row.best.roadKm,
        destination: row.destination,
        note,
        confirmed: false,
      },
    });
  }

  // Most urgent (soonest cutoff) first in the backlog.
  proposals.sort((a, b) => a.cutoff - b.cutoff);

  return { exceptions, proposals: proposals.map((p) => p.proposal) };
}
